import { extractIdsFromParameters } from './mozart.util';

import TimeFormatUtilities from 'utils/TimeFormatUtilities';

import { MOZART_COMPLAINT_REASON } from 'features/mozart-notifications/domain';

import type {
  MozartComplaint,
  MozartNotification,
  MozartNovelty,
  SetNotificationSeenByIdParams,
  SetTurnOffProductParams,
} from 'features/mozart-notifications/domain';
import type { Store } from 'shared/domain';
import type { NotificationsRawResp } from './mozart.response';
import type {
  NotificationSeenByIdReq,
  TurnOffProductByStoreIdReq,
} from './mozart.request';

interface ToMNParams {
  list: NotificationsRawResp;
  stores: Store[];
}
function toMozartNotification(params: ToMNParams): MozartNotification {
  const { list, stores } = params;
  return list.reduce(
    (acc, item) => {
      const _novelty: MozartNovelty = {
        id: item.id,
        createdAt: item.created_at,
        title: item.title,
        description: item.text,
        imageUrl: item.image,
        timestamp: item.created_at,
        seen: item.seen,
        isValidComplaint: null,
      };

      if (item.is_alert) {
        const reason = MOZART_COMPLAINT_REASON[item.created_by];
        const { storeId, productId } = extractIdsFromParameters(
          item.parameters,
        );
        if (reason && !isNaN(storeId) && !isNaN(productId)) {
          _novelty.isValidComplaint = true;
          const _complaint: MozartComplaint = {
            ..._novelty,
            reason,
            storeId,
            productId,
            storeName: stores.find(s => s.id === storeId)?.name ?? '',
            isValidComplaint: true,
            partnerId: item.partner_id,
            alertId: item?.extra_data?.alert_id ?? '',
            comments:
              item?.extra_data?.comments.map(c => ({
                id: c.id ?? NaN,
                reason: c.type ?? '',
                imageUrl: c.image ?? '',
                description: c.comment ?? '',
              })) ?? [],
          };
          acc.complaints.push(_complaint);
        } else {
          _novelty.isValidComplaint = false;
          acc.novelties = [_novelty, ...acc.novelties];
        }
      }

      if (_novelty.isValidComplaint === null) acc.novelties.push(_novelty);

      return acc;
    },
    { novelties: [], complaints: [] } as MozartNotification,
  );
}

function toNotificationSeenById(params: {
  createdAt: SetNotificationSeenByIdParams['createdAt'];
}) {
  const { createdAt } = params;
  return {
    seen_at: TimeFormatUtilities.formatCurrentDateTime(),
    last_seen_created_at: TimeFormatUtilities.formatDateTime(createdAt),
  } as NotificationSeenByIdReq['payload'];
}

function toTurnOffProductByStoreId(params: SetTurnOffProductParams) {
  const { storeId, productId, deactivationOption } = params;
  const payload: TurnOffProductByStoreIdReq['payload'] = {
    store_id: storeId,
    product_id: productId,
    deactivation_type: deactivationOption,
  };
  if (deactivationOption === 'CUSTOM') {
    const oneWeekLater = new Date();
    oneWeekLater.setDate(oneWeekLater.getDate() + 7);
    payload.out_of_stock_activation_date = oneWeekLater.toLocaleDateString(
      'en-CA',
    );
  }
  return payload;
}

export const mozartAdapter = {
  toMozartNotification,
  toNotificationSeenById,
  toTurnOffProductByStoreId,
} as const;
